"use client";

import type { Problem } from "@erlen/core/contracts";

export class ApiError extends Error {
  status: number;
  code?: string;
  detail?: string;
  errors: Array<{ path: string; message: string }>;

  constructor(status: number, problem: Partial<Problem> | null) {
    const message = problem?.detail ?? problem?.title ?? `Request failed (${status})`;
    super(message);
    this.status = status;
    this.code = problem?.code;
    this.detail = problem?.detail;
    this.errors = problem?.errors ?? [];
  }

  /** Field-level message for a form input, if the API reported one. */
  fieldError(path: string): string | undefined {
    return this.errors.find((e) => e.path === path || e.path.endsWith(`.${path}`))?.message;
  }
}

const BASE = "/api/v1";

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "same-origin",
    headers: isForm ? { accept: "application/json" } : { accept: "application/json", ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }
  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined" && !path.startsWith("/auth/")) {
      window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    }
    throw new ApiError(res.status, (json as Partial<Problem>) ?? { title: text || res.statusText });
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => send<T>("GET", path),
  post: <T>(path: string, body?: unknown) => send<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => send<T>("PATCH", path, body),
  put: <T>(path: string, body?: unknown) => send<T>("PUT", path, body),
  delete: <T = void>(path: string) => send<T>("DELETE", path),
  upload: <T>(path: string, form: FormData) => send<T>("POST", path, form),
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.errors.length) return `${err.message} ${err.errors.map((e) => `${e.path}: ${e.message}`).join("; ")}`;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}
